import React, { useState } from 'react';
import { Check, Sparkles, X } from 'lucide-react';
import { useUpgrade } from '../../hooks/useUpgrade';
import { useSubscription } from '../../hooks/useSubscription';
import {
  TIER_LABEL,
  TIER_PRICING,
  annualSavingPercent,
  monthlyPrice,
} from '../../config/tiers';
import type { BillingPeriod, PaidTier } from '../../config/tiers';
import type { UpgradeGate } from '../../config/limits';

/**
 * The shared upgrade modal, opened by any gated action (via useUpgradePrompt).
 * Its button starts the SAME Stripe Checkout flow the header upgrade button uses
 * (useUpgrade → /api/create-checkout-session).
 *
 * BOTH plans are always shown, with the gate's tier preselected. Showing only the
 * required one would hide Plus from anyone who happened to hit a Pro gate first,
 * and the two differ by five dollars — a choice worth letting people see.
 * Preselecting keeps the common case to one click.
 *
 * Slate + bronze, theme-aware (slate-* tokens adapt to the active theme).
 */
interface Props {
  /** What was clicked, and which tier unlocks it. */
  gate: UpgradeGate;
  /** Dismiss ("Maybe later" / backdrop / close). */
  onClose: () => void;
}

const TIER_BLURB: Record<PaidTier, string> = {
  plus: 'Split buy & sell planners, asset comparison, multi-wallet breakdown',
  pro: 'Everything in Plus, plus the full Australian CGT tools and reports',
};

const TIERS: PaidTier[] = ['plus', 'pro'];

/** "A$8" / "A$12.50" — whole dollars stay whole. */
function aud(amount: number): string {
  return `A$${amount % 1 === 0 ? amount : amount.toFixed(2)}`;
}

export function UpgradePrompt({ gate, onClose }: Props) {
  const { startUpgrade, loading, error } = useUpgrade();
  const { refetch } = useSubscription();
  // Set when an EXISTING subscription was changed in place. Checkout redirects
  // away, so this state only ever applies to the upgrade path.
  const [done, setDone] = useState<{ tier: PaidTier } | null>(null);
  const [period, setPeriod] = useState<BillingPeriod>('annual');
  // The gate's tier when it names one; otherwise Plus, the entry paid tier — a
  // generic "Upgrade" click has not asked for the more expensive plan.
  const [tier, setTier] = useState<PaidTier>(gate.tier ?? 'plus');

  const submit = async () => {
    const result = await startUpgrade(tier, period);
    if (result.kind === 'upgraded') {
      // Safe to read entitlement right away: the server wrote the new row before
      // responding, precisely because the webhook lands too late for this refetch
      // to see it. The webhook still confirms the same values a moment later.
      await refetch();
      setDone({ tier: result.tier });
    }
    // 'redirected' navigates away; 'failed' surfaces through `error`.
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-slate-950 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-sm relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6">
          <div className="flex items-center gap-2.5 mb-3">
            <span
              className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
              style={{ backgroundColor: 'rgba(212,180,131,0.14)' }}
            >
              <Sparkles className="w-4 h-4" style={{ color: '#d4b483' }} />
            </span>
            <h2 className="text-base font-semibold text-slate-100">
              {done
                ? `You are on ${TIER_LABEL[done.tier]}`
                : gate.tier
                  ? `Upgrade to ${TIER_LABEL[gate.tier]}`
                  : 'Choose your plan'}
            </h2>
          </div>

          {done ? (
            <>
              <p className="text-sm text-slate-400 leading-relaxed">
                Everything on {TIER_LABEL[done.tier]} is available right now. Nothing has been
                charged today. The difference for the rest of this billing period will appear
                on your next invoice, and your renewal date has not changed.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="mt-5 w-full flex items-center justify-center gap-2 rounded-lg text-sm font-semibold py-2.5"
                style={{ backgroundColor: '#d4b483', color: '#1a1410' }}
              >
                Done
              </button>
            </>
          ) : (
          <>
          <p className="text-sm text-slate-400 leading-relaxed">{gate.message}</p>

          {/* Billing period — shared by both plans */}
          <div className="mt-4">
            <div className="inline-flex items-center gap-1 rounded-full p-1 bg-slate-800 border border-slate-700">
              {(['monthly', 'annual'] as const).map((p) => {
                const active = period === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPeriod(p)}
                    className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                      active ? 'bg-slate-100 text-slate-900' : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {p === 'monthly' ? 'Monthly' : 'Annual'}
                    {p === 'annual' && (
                      <span
                        className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                        style={{ backgroundColor: '#d4b483', color: '#1a1410' }}
                      >
                        Save {annualSavingPercent('pro')}%
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Plan cards — the gate's tier starts selected */}
          <div className="mt-4 flex flex-col gap-2">
            {TIERS.map((t) => {
              const selected = tier === t;
              const required = gate.tier === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTier(t)}
                  aria-pressed={selected}
                  className="w-full text-left rounded-xl px-3.5 py-3 transition-colors"
                  style={{
                    border: selected
                      ? '1px solid rgba(212,180,131,0.55)'
                      : '0.5px solid rgb(var(--sl-700))',
                    background: selected ? 'rgba(212,180,131,0.07)' : 'transparent',
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="flex items-center justify-center w-4 h-4 rounded-full flex-shrink-0"
                        style={{
                          border: selected ? 'none' : '1px solid rgb(var(--sl-600))',
                          background: selected ? '#d4b483' : 'transparent',
                        }}
                      >
                        {selected && (
                          <Check className="w-2.5 h-2.5" style={{ color: '#1a1410' }} strokeWidth={3} />
                        )}
                      </span>
                      <span className="text-sm font-semibold text-slate-100">{TIER_LABEL[t]}</span>
                      {required && (
                        <span
                          className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                          style={{ backgroundColor: 'rgba(212,180,131,0.16)', color: '#d4b483' }}
                        >
                          Needed for this
                        </span>
                      )}
                    </div>
                    <span className="text-sm font-semibold text-slate-100 tabular-nums whitespace-nowrap">
                      {aud(monthlyPrice(t, period))}
                      <span className="text-xs font-normal text-slate-400">/mo</span>
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{TIER_BLURB[t]}</p>
                  {period === 'annual' && (
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      A${TIER_PRICING[t].annual} billed annually
                    </p>
                  )}
                </button>
              );
            })}
          </div>

          {error && <p className="mt-3 text-xs text-red-400">{error}</p>}

          <div className="mt-5 flex flex-col gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={loading}
              className="flex items-center justify-center gap-2 rounded-lg text-sm font-semibold py-2.5 transition-transform hover:scale-[1.01] disabled:opacity-60 disabled:hover:scale-100"
              style={{ backgroundColor: '#d4b483', color: '#1a1410' }}
            >
              <Sparkles className="w-3.5 h-3.5" />
              {loading ? 'Starting…' : `Get ${TIER_LABEL[tier]}`}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg text-sm font-medium py-2.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
            >
              Maybe later
            </button>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
